import cn from "classnames";
import { AirStatusesModel } from "entities/shared/airCard.types";
import { RailStatusesModel } from "entities/shared/railCard.types";
import { RoadStatusesModel } from "entities/shared/roadCard.types";
import { SeaReferenceTypesModel, SeaStatusesModel } from "entities/shared/seaCard.types";
import { DateTimeModel, ProgressLineProps, ShipmentType } from "entities/shared/shipmentCard.types";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { CardDirection } from "./components/CardDirection";
import { CardFooter } from "./components/CardFooter";
import { CardHeader } from "./components/CardHeader";
import { CardRow } from "./components/CardRow";
import { ProgressLine } from "./components/ProgressLine";
import styles from "./Card.module.scss";

import { CardContextType, CardProvider } from "./Card.context";

type CardBaseProps = CardContextType & {
  className?: string;
  reference?: Nullable<string>;
};

export type CardHeaderStatus =
  | ValueOf<typeof SeaStatusesModel>
  | ValueOf<typeof AirStatusesModel>
  | ValueOf<typeof RailStatusesModel>
  | ValueOf<typeof RoadStatusesModel>;

// Base type that combines common properties for both Sea and Air cards
type CardProps = CardContextType & {
  reference?: Nullable<string>;
  className?: string;
  status: CardHeaderStatus | null; // Allow null to support both Sea and Air status types
  shipmentType: ValueOf<typeof ShipmentType>;
  directionFrom: Nullable<string>;
  directionTo: Nullable<string>;
  etd: Nullable<DateTimeModel>;
  eta: Nullable<DateTimeModel>;
  atd: Nullable<DateTimeModel>;
  ata: Nullable<DateTimeModel>;
  progressLine: ProgressLineProps;
  tags?: string[];
  // Sea-specific properties (optional to support both Sea and Air cards)
  referenceType?: ValueOf<typeof SeaReferenceTypesModel> | null;
  containersAmount?: number;
  containersSizeTypes?: string[];
  scac?: string | null;
  carrierName?: Nullable<string>;
  predictiveETA?: Nullable<DateTimeModel>;
  // Air-specific properties (optional to support both types)
  pieces?: Nullable<string>;
  // Rail-specific properties (optional to support both types)
  count?: number;
};

type CardComponentType = RFC<CardBaseProps> & {
  Row: typeof CardRow;
  Header: typeof CardHeader;
  Footer: typeof CardFooter;
  Direction: typeof CardDirection;
  Progress: typeof ProgressLine;
};

export const CardBase: CardComponentType = ({
  onOpen,
  onDelete,
  onFollow,
  onUpdate,
  reference,
  className,
  children,
  canUpdate,
  isUpdating,
  isFollowLoading,
  isDeleteProcessing,
}) => {
  const hasOnOpenHandler = !!onOpen;

  return (
    <div className={cn(styles.card, hasOnOpenHandler && styles.hasOnOpenHandler, className)} data-reference={reference}>
      {hasOnOpenHandler && <div className={styles.overlayOpener} onClick={onOpen} />}
      <CardProvider
        value={{ onOpen, onFollow, onDelete, onUpdate, canUpdate, isUpdating, isFollowLoading, isDeleteProcessing }}
      >
        {children}
      </CardProvider>
    </div>
  );
};

export const Card: RFC<CardProps> = ({
  shipmentType,
  onOpen,
  onDelete,
  onFollow,
  onUpdate,
  reference,
  className,
  isDeleteProcessing,
  isFollowLoading,
  canUpdate,
  isUpdating,
  status,
  directionFrom,
  directionTo,
  etd,
  eta,
  atd,
  ata,
  progressLine,
  tags,
  referenceType,
  containersAmount,
  containersSizeTypes,
  scac,
  carrierName,
  predictiveETA,
  pieces,
  count,
}) => {
  const { rotation = "horizontal", status: progressLineStatus, ...restProgressLineProps } = progressLine;

  return (
    <CardBase
      onOpen={onOpen}
      onDelete={onDelete}
      onFollow={onFollow}
      onUpdate={onUpdate}
      reference={reference}
      className={className}
      canUpdate={canUpdate}
      isUpdating={isUpdating}
      isFollowLoading={isFollowLoading}
      isDeleteProcessing={isDeleteProcessing}
    >
      <CardBase.Header
        shipmentType={shipmentType}
        referenceType={referenceType}
        containersAmount={containersAmount}
        reference={reference}
        status={status}
        containersSizeTypes={containersSizeTypes}
        scac={scac}
        carrierName={carrierName}
        tags={tags}
        pieces={pieces}
        count={count}
      />
      <CardBase.Direction from={directionFrom} to={directionTo} reference={reference} />
      <CardBase.Row>
        <ProgressLine
          rotation={rotation}
          status={progressLineStatus}
          shipmentType={shipmentType}
          {...restProgressLineProps}
        />
      </CardBase.Row>
      <CardBase.Footer etd={etd} eta={eta} atd={atd} ata={ata} predictiveETA={predictiveETA} reference={reference} />
    </CardBase>
  );
};

CardBase.displayName = CardBase.name;
Card.displayName = Card.name;

CardBase.Row = CardRow;
CardBase.Direction = CardDirection;
CardBase.Progress = ProgressLine;
CardBase.Header = CardHeader;
CardBase.Footer = CardFooter;
